import {
    CalendarDaysIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    ReceiptPercentIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StatCard } from './StatusCards';
import type { InvoiceStatusSummary } from '../../services/billing';

// Clickable status stat cards above the invoices table, count-first like the
// charge cards. The transient pending/created statuses get no card of their
// own (the consumer cron moves through them in seconds) but still count into
// All; the status dropdown covers them when needed. The summary comes from
// the list endpoint and respects the practice/search filters but not the
// status filter, so numbers stay stable while filtering.

interface CardConfig {
    status: string | null; // null = all invoices
    label: string;
    unit: string;
    icon: React.ReactNode;
}

const ICON_CLASS = 'h-3.5 w-3.5';

const CARD_CONFIGS: CardConfig[] = [
    {
        status: null,
        label: 'All invoices',
        unit: 'total',
        icon: <Squares2X2Icon className={ICON_CLASS} />
    },
    {
        status: 'open',
        label: 'Open',
        unit: 'awaiting payment',
        icon: <ClockIcon className={ICON_CLASS} />
    },
    {
        status: 'partial',
        label: 'Partial',
        unit: 'partly paid',
        icon: <ReceiptPercentIcon className={ICON_CLASS} />
    },
    {
        status: 'overdue',
        label: 'Overdue',
        unit: 'past due',
        icon: <CalendarDaysIcon className={ICON_CLASS} />
    },
    {
        status: 'error',
        label: 'Error',
        unit: 'failed',
        icon: <ExclamationTriangleIcon className={ICON_CLASS} />
    },
    {
        status: 'void',
        label: 'Void',
        unit: 'invoices',
        icon: <NoSymbolIcon className={ICON_CLASS} />
    }
];

interface InvoiceStatusCardsProps {
    statusSummary: Record<string, InvoiceStatusSummary>;
    selected: string[];
    onChange: (next: string[]) => void;
    // Overridable so an embedded host (e.g. the Accounts practice detail
    // Invoices tab) that already sits inside its own page padding isn't
    // double-indented.
    className?: string;
}

export function InvoiceStatusCards({
    statusSummary,
    selected,
    onChange,
    className = 'px-8 pt-4'
}: InvoiceStatusCardsProps) {
    // 'overdue' overlaps the stored statuses, so it stays out of the All total.
    const allCount = Object.entries(statusSummary)
        .filter(([key]) => key !== 'overdue')
        .reduce((sum, [, e]) => sum + e.count, 0);

    return (
        <div className={`flex items-stretch gap-3 ${className}`}>
            {CARD_CONFIGS.map((config) => {
                const isActive =
                    config.status === null
                        ? selected.length === 0
                        : selected.length === 1 && selected[0] === config.status;
                const count =
                    config.status === null ? allCount : (statusSummary[config.status]?.count ?? 0);
                return (
                    <StatCard
                        key={config.status ?? 'all'}
                        label={config.label}
                        icon={config.icon}
                        value={String(count)}
                        unit={config.unit}
                        isActive={isActive}
                        onClick={() => {
                            if (config.status === null || isActive) {
                                onChange([]);
                            } else {
                                onChange([config.status]);
                            }
                        }}
                    />
                );
            })}
        </div>
    );
}
